import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, switchMap, takeUntil, tap } from 'rxjs/operators';

import { SetActiveAccountId, SetActiveWorkspaceId } from '../../../services/active/active.actions';
import { SetBasicFetched } from '../../sync/sync.actions';
import { TUISettings } from '../../ui/ui.model';

import { PromptService } from 'src/app/project/components/prompt/prompt.service';
import { AccountService } from '../../account/account-service/account.service';
import { SidePanelService } from '../../layout/side-panel/side-panel.service';
import { EditPointService } from '../../points/point-full-modal/edit-point.service';
import { PreferencesService } from '../../preferences/preferences-service/preferences.service';
import { SyncService } from '../../sync/sync.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { SiteDataService, TSiteData } from '../site-data.service';
import { SiteTableColumnsService } from '../site-table/site-table-columns.service';
import { GET_TIMELINE$ } from './timeline.ui.store';

import { Title } from '@angular/platform-browser';
import { TranslationPipe } from 'src/app/project/features/translate/translation.pipe';
import { TSidePanel } from 'src/app/project/modules/layout/side-panel/side-panel.model';
import { EInfiniteScrollApiKey } from 'src/app/project/services/infinite-scrolling/infinite-scrolling.consts';
import { EStore } from '../../../shared/enums/store.enum';
import { PlanService } from '../../plan/plan.service';
import { PointSearchService } from '../../points/point-search/point-search.service';
import { TableLoadingService } from '../site-table/custom-table/table-loading.service';

@Component({
  selector: 'pp-site-timeline',
  templateUrl: './site-timeline.component.html',
  styleUrls: ['./site-timeline.component.scss'],
  standalone: false,
})
export class SiteTimelineComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  site: TSiteData = this.siteDataService.getSite();
  sidePanel: TSidePanel = this.sidePanelService.getSidePanel();

  ui$: Observable<TUISettings>;
  ui: TUISettings;

  dataFetched = false;
  timelinePointsLength = 0;
  isTableLoading: Observable<boolean>;
  private subscribedToTimeline = false;

  constructor(
    private store: Store<{
      ui: TUISettings;
    }>,
    private siteDataService: SiteDataService,
    private sidePanelService: SidePanelService,
    private workspaceService: WorkspaceService,
    private syncService: SyncService,
    private preferencesService: PreferencesService,
    private accountService: AccountService,
    private siteTableColumnsService: SiteTableColumnsService,
    private editPointService: EditPointService,
    private translationPipe: TranslationPipe,
    private promptService: PromptService,
    private router: Router,
    private titleService: Title,
    private pointSearchService: PointSearchService,
    private tableLoadingService: TableLoadingService,
    private planService: PlanService,
  ) {
    this.isTableLoading = tableLoadingService.isTableLoading$;
    this.ui$ = this.store.pipe(select(EStore.UI));
  }

  ngOnInit() {
    this.titleService.setTitle('Loading... | Pinpoint Works');

    const workspaces = this.workspaceService.getWorkspaces();
    const accounts = this.accountService.getAccounts();
    this.store.dispatch(new SetActiveAccountId(null));
    this.sidePanelService.initSidePanel({ expandedInitially: false });

    if (!Array.isArray(accounts) || Object.entries(workspaces).length === 0) {
      this.store.dispatch(new SetActiveWorkspaceId(null));

      this.syncService
        .firstLoad()
        .pipe(
          takeUntil(this.destroy$),
          tap(() => {
            this.generateWorkspaces();
          }),
        )
        .subscribe();
    } else {
      this.store.dispatch(new SetActiveWorkspaceId(null));

      this.generateWorkspaces();
    }

    this.ui$.pipe(takeUntil(this.destroy$)).subscribe((ui) => {
      this.ui = ui;
    });
  }

  ngOnDestroy() {
    this.siteDataService.clearSite();
    this.editPointService.hideModal();
    this.planService.clearSitePlanCache();
    this.destroy$.next();
  }

  private subscribeToTimelineUpdates(): void {
    if (this.subscribedToTimeline) {
      return;
    }

    this.subscribedToTimeline = true;

    GET_TIMELINE$()
      .pipe(
        takeUntil(this.destroy$),
        filter((timeline) => timeline !== null),
        switchMap((timeline) => timeline.pointsUpdated$),
      )
      .subscribe((count) => {
        this.timelinePointsLength = count;
      });
  }

  updateTitle(): void {
    this.titleService.setTitle('Timeline | Pinpoint Works');
  }

  checkTimelineAccess(): boolean {
    return !!this.accountService
      .getAccounts()
      .some((account) => !!account.accountFeatures.timeline);
  }

  redirectFromTimeline(): void {
    const prompt = this.translationPipe.transform('prompt_timeline_access_denied');

    this.promptService.showError(prompt);
    this.router.navigate(['/site']);
  }

  generateTimeline(): void {
    this.updateTitle();
    this.siteTableColumnsService.initAllColumns();

    this.dataFetched = true;

    this.store.dispatch(new SetBasicFetched(true));
    this.subscribeToTimelineUpdates();
  }

  generateWorkspaces(): void {
    this.pointSearchService.setAPIKey(EInfiniteScrollApiKey.TIMELINE_POINTS);

    this.workspaceService
      .generateWorkspaces()
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() => this.preferencesService.fetchPreferences()),
        tap(() => {
          const timelineEnabled = this.checkTimelineAccess();

          if (!timelineEnabled) {
            this.redirectFromTimeline();
          } else {
            this.generateTimeline();
          }
        }),
      )
      .subscribe();
  }
}
